import {
    DEFAULT_TIMEOUT_MS,
    ERROR_CATEGORIES,
    PROTOCOL_VERSION,
} from "./constants.js";
import { getTraceId, getRoute, safeClone } from "./runtime.js";

const pendingRequests = new Map();

function hasAndroidBridge() {
    return !!(
        window.ShellBridge &&
        typeof window.ShellBridge.postMessage === "function"
    );
}

function hasIosBridge() {
    return !!(
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.ShellBridge &&
        typeof window.webkit.messageHandlers.ShellBridge.postMessage === "function"
    );
}

function hasNativeBridge() {
    return hasAndroidBridge() || hasIosBridge();
}

function currentTransport() {
    if (hasAndroidBridge()) return "android";
    if (hasIosBridge()) return "ios";
    return "web";
}

function newId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function now() {
    return Date.now();
}

function normalizeSource(source = {}) {
    const route = source.route || getRoute(source) || undefined;

    return {
        mfe: source.mfe,
        version: source.version,
        island: source.island,
        instanceId: source.instanceId,
        route: route ? safeClone(route, undefined) : undefined,
    };
}

function buildEnvelope(type, payload, options = {}) {
    const source = normalizeSource(options.source || {});

    return {
        id: options.expectsResponse ? newId() : undefined,
        type,
        payload: safeClone(payload || {}, {}),
        source,
        protocolVersion: PROTOCOL_VERSION,
        meta: {
            expectsResponse: !!options.expectsResponse,
            fireAndForget: !options.expectsResponse,
            transport: currentTransport(),
            timestamp: now(),
            traceId: options.meta?.traceId || getTraceId(source) || undefined,
            ...options.meta,
        },
    };
}

function postToNativeBridge(message) {
    const serialized = JSON.stringify(message);

    if (hasAndroidBridge()) {
        window.ShellBridge.postMessage(serialized);
        return true;
    }

    if (hasIosBridge()) {
        window.webkit.messageHandlers.ShellBridge.postMessage(message);
        return true;
    }

    return false;
}

function dispatchWebInbound(message) {
    window.dispatchEvent(
        new CustomEvent("mfe:message", {
            detail: message,
        })
    );
}

function sendEnvelope(message) {
    if (postToNativeBridge(message)) return;
    dispatchWebInbound(message);
}

export function notify(type, payload = {}, options = {}) {
    const message = buildEnvelope(type, payload, {
        ...options,
        expectsResponse: false,
    });

    sendEnvelope(message);
    return message;
}

export function request(type, payload = {}, options = {}) {
    const timeoutMs =
        typeof options.timeoutMs === "number"
            ? options.timeoutMs
            : DEFAULT_TIMEOUT_MS;

    return new Promise((resolve, reject) => {
        const message = buildEnvelope(type, payload, {
            ...options,
            expectsResponse: true,
        });

        pendingRequests.set(message.id, { resolve, reject });
        sendEnvelope(message);

        const timeoutId = setTimeout(() => {
            if (!pendingRequests.has(message.id)) return;

            pendingRequests.delete(message.id);
            reject({
                code: "TIMEOUT",
                message: `Shell request timeout: ${type}`,
                category: ERROR_CATEGORIES.TIMEOUT,
            });
        }, timeoutMs);

        pendingRequests.set(message.id, {
            resolve,
            reject,
            timeoutId,
        });
    });
}

export function handleBridgeResponse(rawMessage) {
    const msg = normalizeBridgeResponse(rawMessage);
    if (!msg || !msg.id) return false;
    if (!pendingRequests.has(msg.id)) return false;

    const pending = pendingRequests.get(msg.id);
    pendingRequests.delete(msg.id);

    if (pending.timeoutId) {
        clearTimeout(pending.timeoutId);
    }

    if (msg.ok) {
        pending.resolve(msg.data ?? null);
    } else {
        pending.reject(
            msg.error || {
                code: "UNKNOWN_ERROR",
                message: "Unknown Shell bridge error",
                category: ERROR_CATEGORIES.UNKNOWN,
            }
        );
    }

    return true;
}

function normalizeBridgeResponse(rawMessage) {
    if (!rawMessage) return null;

    if (typeof rawMessage === "string") {
        try {
            return JSON.parse(rawMessage);
        } catch {
            return null;
        }
    }

    if (typeof rawMessage === "object") {
        return rawMessage;
    }

    return null;
}

export function isNative() {
    return hasNativeBridge();
}
